// frontend/src/hooks/usePermissions.js
import { useAuth } from '../contexts/AuthContext';

export const usePermissions = () => {
  const { currentUser } = useAuth();

  const getDefaultPermissions = (role) => {
    const permissions = {
      admin: {
        users: { create: true, edit: true, delete: true, view: true },
        clients: { create: true, edit: true, delete: true, view: true },
        invoices: { create: true, edit: true, delete: true, view: true },
        payments: { create: true, edit: true, delete: true, view: true, export: true },
        tasks: { create: true, edit: true, delete: true, view: true },
        settings: { create: true, edit: true, delete: true, view: true },
      },
      collaborator: {
        users: { create: false, edit: false, delete: false, view: false },
        clients: { create: true, edit: true, delete: true, view: true },
        invoices: { create: true, edit: true, delete: false, view: true },
        payments: { create: true, edit: true, delete: false, view: true },
        tasks: { create: true, edit: true, delete: true, view: true },
        settings: { create: false, edit: false, delete: false, view: false },
      },
      client: {
        users: { create: false, edit: false, delete: false, view: false },
        clients: { create: true, edit: true, delete: true, view: true },
        suppliers: { create: true, edit: true, delete: false, view: true },
        invoices: { create: true, edit: true, delete: true, view: true },
        payments: { create: true, edit: true, delete: true, view: true, export: true },
        purchaseInvoices: { create: true, edit: true, delete: true, view: true },
        purchasePayments: { create: true, edit: true, delete: true, view: true },
        tasks: { create: false, edit: false, delete: false, view: false },
        settings: { create: false, edit: false, delete: false, view: false },
      },
    };
    return permissions[role] || {};
  };

  const hasPermission = (module, action) => {
    if (!currentUser) return false;
    if (currentUser.role === 'admin') return true;

    if (currentUser.permissions?.[module]?.[action] !== undefined) {
      return currentUser.permissions[module][action] === true;
    }

    const defaults = getDefaultPermissions(currentUser.role);
    if (defaults?.[module]?.[action] !== undefined) return defaults[module][action] === true;

    return false;
  };

  const normalizeId = (value) => {
    if (!value) return null;
    if (typeof value === 'string') return value;
    if (value._id) return value._id.toString();
    return value.toString?.() || null;
  };

  const canAccessClient = (clientId) => {
    if (!currentUser || !clientId) return false;
    if (currentUser.role === 'admin') return true;
    if (currentUser.role === 'client') return true;

    const targetId = normalizeId(clientId);

    if (currentUser.role === 'collaborator') {
      const assignedIds = (currentUser.assignedClients || []).map((c) => normalizeId(c)).filter(Boolean);
      return assignedIds.includes(targetId);
    }

    return false;
  };

  const canModifyResource = (resource, resourceType) => {
    if (!currentUser || !resource) return false;
    if (currentUser.role === 'admin') return true;
    if (currentUser.role === 'client') return true;
    if (!hasPermission(resourceType, 'edit')) return false;

    if (['client', 'invoice', 'payment'].includes(resourceType)) {
      const clientId =
        resourceType === 'client'
          ? (resource._id || resource.id)
          : (resource.clientId?._id || resource.clientId || resource.client?._id || resource.client);

      return canAccessClient(clientId);
    }

    return true;
  };

  const canDeleteResource = (resource, resourceType) => {
    if (!currentUser || !resource) return false;
    if (currentUser.role === 'admin') return true;
    if (currentUser.role === 'client') return true;
    if (!hasPermission(resourceType, 'delete')) return false;

    if (['client', 'invoice', 'payment'].includes(resourceType)) {
      const clientId =
        resourceType === 'client'
          ? (resource._id || resource.id)
          : (resource.clientId?._id || resource.clientId || resource.client?._id || resource.client);

      return canAccessClient(clientId);
    }

    return true;
  };

  const canViewResource = (resource, resourceType) => {
    if (!currentUser || !resource) return false;
    if (currentUser.role === 'admin') return true;
    if (!hasPermission(resourceType, 'view')) return false;

    if (['client', 'invoice', 'payment'].includes(resourceType)) {
      const clientId =
        resourceType === 'client'
          ? (resource._id || resource.id)
          : (resource.clientId?._id || resource.clientId || resource.client?._id || resource.client);

      if (!clientId) return true;
      return canAccessClient(clientId);
    }

    return true;
  };

  return {
    currentUser,
    hasPermission,
    canAccessClient,
    canViewResource,
    canModifyResource,
    canDeleteResource,
  };
};
